/**
 * Author: Romain Hapi Sr.
 * Version: 1.0
 * Date: 2026-09-11
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import SEO from "../components/SEO";
import MEDIA from "../lib/media";
import { buildLegal } from "../lib/legal";
import { Mail, MapPin, Phone, ShieldCheck, FileText } from "lucide-react";

/**
 * Pages juridiques du site UK : confidentialite et conditions d'utilisation.
 *
 * Un seul composant sert les deux documents. Leur mise en page est identique —
 * un chapo, un sommaire, des sections numerotees — et seul le contenu change ;
 * deux fichiers jumeaux auraient diverge des la premiere retouche de maquette.
 * Les textes eux-memes vivent dans `lib/legal.js`, partages avec le site Tchad.
 *
 * Le sommaire reste colle au defilement sur grand ecran : ces documents font
 * plusieurs ecrans de haut, et sans repere on ne sait plus ou l'on en est.
 */
const Legal = ({ doc }) => {
  const { t, lang } = useLang();
  if (!t) return null;

  const legal = buildLegal(doc, "uk", lang);
  if (!legal) return null;

  const Icon = doc === "privacy" ? ShieldCheck : FileText;

  return (
    <main data-testid={`legal-${doc}-page`} className="pt-16 md:pt-20">
      <SEO
        title={`${legal.title} — Prenim`}
        description={legal.lead.slice(0, 160)}
        lang={lang}
      />

      {/* Banniere */}
      <section className="relative overflow-hidden bg-slate-950 py-20 text-white lg:py-28">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url('${MEDIA.hero_background}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/90 to-slate-950/60" />
        <div className="absolute inset-0 bg-grid-slate opacity-30" />

        <div className="relative max-w-site mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-medium text-cyan-300 backdrop-blur-sm">
            <Icon className="h-3.5 w-3.5" />
            {legal.kindLabel}
          </span>
          <h1 className="mt-6 max-w-3xl text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            {legal.title}
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-300">
            {legal.lead}
          </p>
          <p className="mt-6 text-sm text-slate-400">
            {legal.updatedLabel} : <time dateTime={legal.updated}>{legal.updated}</time>
          </p>
        </div>
      </section>

      {/* Corps du document */}
      <section className="py-16 lg:py-24">
        <div className="max-w-site mx-auto grid gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-8">
          {/* Sommaire. Il passe au-dessus du texte sous `lg`, ou une colonne
              laterale serait trop etroite pour rester lisible. */}
          <aside className="lg:col-span-4">
            <nav
              aria-label={legal.tocLabel}
              className="rounded-2xl border border-slate-200 bg-slate-50 p-6 lg:sticky lg:top-28"
            >
              <h2 className="text-xs font-semibold uppercase tracking-widest text-blue-600">
                {legal.tocLabel}
              </h2>
              <ol className="mt-5 space-y-3">
                {legal.sections.map((s, i) => (
                  <li key={s.key}>
                    <a
                      href={`#${s.key}`}
                      className="group flex gap-3 text-sm text-slate-600 transition-colors hover:text-blue-600"
                    >
                      <span className="font-mono text-xs text-slate-400 transition-colors group-hover:text-blue-600">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-start">{s.title}</span>
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          </aside>

          <div className="lg:col-span-8">
            <div className="space-y-12">
              {legal.sections.map((s, i) => (
                <article
                  key={s.key}
                  id={s.key}
                  data-testid={`legal-section-${s.key}`}
                  className="scroll-mt-28 border-s-2 border-slate-200 ps-6"
                >
                  <span className="font-mono text-xs font-semibold text-blue-600">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h2 className="mt-2 text-2xl font-bold tracking-tight text-slate-950">
                    {s.title}
                  </h2>
                  <p className="mt-4 leading-relaxed text-slate-600">{s.body}</p>
                </article>
              ))}
            </div>

            {/* Carte de contact : un document juridique doit se terminer sur une
                adresse a qui ecrire, pas sur le dernier article. */}
            <div className="mt-16 overflow-hidden rounded-2xl border border-slate-200">
              <img
                src={MEDIA.digital_transformation}
                alt=""
                aria-hidden="true"
                loading="lazy"
                className="h-40 w-full object-cover sm:h-48"
              />
              <div className="bg-slate-950 p-8 text-white">
                <p className="no-justify text-sm leading-relaxed text-slate-300">
                  {legal.notice}
                </p>
                <div className="mt-6 grid gap-3 text-sm sm:grid-cols-2">
                  <p className="no-justify flex items-start gap-2 text-slate-300">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" />
                    <span>{legal.address}</span>
                  </p>
                  <p className="flex items-center gap-2 text-slate-300">
                    <Mail className="h-4 w-4 shrink-0 text-cyan-400" />
                    <a href={`mailto:${legal.entity.email}`} className="hover:text-cyan-400">
                      {legal.entity.email}
                    </a>
                  </p>
                  <p className="flex items-center gap-2 text-slate-300">
                    <Phone className="h-4 w-4 shrink-0 text-cyan-400" />
                    <a
                      href={`tel:${legal.entity.telephone.replace(/\s/g, "")}`}
                      className="hover:text-cyan-400"
                    >
                      {legal.entity.telephone}
                    </a>
                  </p>
                </div>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to={doc === "privacy" ? "/terms" : "/privacy"}
                    data-testid="legal-sibling-link"
                    className="inline-flex items-center gap-2 rounded-full border-2 border-white/30 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:border-cyan-400 hover:text-cyan-400"
                  >
                    {doc === "privacy"
                      ? buildLegal("terms", "uk", lang).title
                      : buildLegal("privacy", "uk", lang).title}
                  </Link>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-500"
                  >
                    {t.navigation.contact}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Legal;
